
import type { ReactNode } from 'react';
import { Card, Stack, Text } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import { CalendarToolbar } from '@client/shared/calendar/CalendarToolbar.tsx';
import type { CalendarRange } from '@client/shared/hooks/use-calendar-view.ts';
import type { SlotCalendarRange } from './slot-grid/index.ts';
import { CalendarLegend, type CalendarLegendItem } from './CalendarLegend.tsx';
import styles from './Calendar.module.css';

export interface CalendarFrameProps {
  zone: string;
  anchor: Date;
  range: SlotCalendarRange;
  onStep: (delta: -1 | 1) => void;
  onToday: () => void;
  onRangeChange?: (next: CalendarRange) => void;
  canStepBack?: boolean;
  canStepForward?: boolean;
  filters?: ReactNode;
  instruction?: ReactNode;
  legend?: readonly CalendarLegendItem[];
  loading?: boolean;
  loadingLabel?: string;
  error?: boolean;
  errorLabel?: { title: string; detail: string };
  children: ReactNode;
  className?: string;
}

export function Calendar({
  zone,
  anchor,
  range,
  onStep,
  onToday,
  onRangeChange,
  canStepBack,
  canStepForward,
  filters,
  instruction,
  legend,
  loading = false,
  loadingLabel = 'Loading…',
  error = false,
  errorLabel,
  children,
  className,
}: CalendarFrameProps) {
  return (
    <div className={cx(styles.frame, className)}>
      <Card pad="none" spine className={styles.calendar}>
        <CalendarToolbar
          anchor={anchor}
          zone={zone}
          range={range}
          onStep={onStep}
          onToday={onToday}
          onRangeChange={onRangeChange}
          canStepBack={canStepBack}
          canStepForward={canStepForward}
        >
          {filters}
        </CalendarToolbar>

        {instruction != null && <div className={styles.instruction}>{instruction}</div>}

        {loading ? (
          <div className={styles.state}>
            <Text tone="soft" size="sm">
              {loadingLabel}
            </Text>
          </div>
        ) : error ? (
          <div className={styles.state}>
            <Stack gap={2}>
              <Text tone="danger" weight="medium">
                {errorLabel?.title ?? 'Something went wrong'}
              </Text>
              <Text tone="soft" size="sm">
                {errorLabel?.detail ?? 'Please refresh the page to try again.'}
              </Text>
            </Stack>
          </div>
        ) : (
          children
        )}
      </Card>

      {!loading && !error && legend != null && legend.length > 0 && (
        <CalendarLegend items={legend} className={styles.legend} />
      )}
    </div>
  );
}
